/**
 * System Constants
 *
 * Centralized constants for system-level functionality shared across all applications.
 * Includes system field definitions, SQL identifiers, and query prefixes.
 *
 * Used by:
 * - apps/api (query building, record processing)
 * - apps/duckdb-api (query execution, field filtering)
 * - apps/app (frontend data display)
 *
 * @module @loader/shared-types/constants/system
 */

// ============================================================================
// SQL Identifiers
// ============================================================================

/**
 * Alias used for calculated/derived columns in SQL CTEs
 *
 * Used to distinguish calculated columns from base table columns
 * in query building and result processing.
 */
export const CALCULATED_TABLE_ALIAS = '__calculated__';

/**
 * Prefix for system fields
 *
 * All system fields (metadata) start with underscore.
 * Used to filter system fields from user-facing data.
 */
export const SYSTEM_FIELD_PREFIX = '_';

// ============================================================================
// System Field Definitions
// ============================================================================

/**
 * System field names as a constant object
 * Use `as const` for type safety and auto-completion
 */
export const SYSTEM_FIELDS = {
  /** Unique record identifier (UUID) - Primary key for each record */
  RECORD_ID: '_record_id',

  /** Version identifier (UUID) - Tracks which version created this record */
  VERSION_ID: '_version_id',

  /** Record creation timestamp (ISO 8601) - When the record was ingested */
  CREATED_AT: '_created_at',

  /** Content hash (SHA-256) - Used for change detection and deduplication */
  CONTENT_HASH: '_content_hash',

  /** Active status flag (boolean) - Whether this is the current version of the record */
  IS_ACTIVE: '_is_active',

  /** Business key (string) - Composite key for record matching across versions */
  BUSINESS_KEY: '_business_key',
} as const;

/**
 * Array of all system field names
 * Useful for iteration and validation
 */
export const SYSTEM_FIELD_NAMES = Object.values(SYSTEM_FIELDS);

/**
 * Set of all system field names for O(1) lookup performance
 * Used internally by helper functions
 */
const SYSTEM_FIELD_SET = new Set<string>(SYSTEM_FIELD_NAMES);

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if a field name is a system field
 *
 * @param fieldName - Field name to check
 * @returns True if field is a system field (starts with underscore)
 *
 * @example
 * ```typescript
 * isSystemField('_record_id')  // true
 * isSystemField('email')        // false
 * isSystemField('_custom')      // true (any underscore-prefixed field)
 * ```
 */
export function isSystemField(fieldName: string): boolean {
  // Fast path: Check known system fields first (O(1))
  if (SYSTEM_FIELD_SET.has(fieldName)) {
    return true;
  }

  // Fallback: Any field starting with underscore is treated as system field
  // This allows for future extensibility without code changes
  return fieldName.startsWith(SYSTEM_FIELD_PREFIX);
}

/**
 * Filter system fields from a single record
 *
 * @param record - Record object with mixed user and system fields
 * @param includeSystemFields - If true, keeps system fields (default: false)
 * @returns Record with only user fields (unless includeSystemFields is true)
 *
 * @example
 * ```typescript
 * const record = {
 *   _record_id: '123',
 *   _version_id: '456',
 *   name: 'John',
 *   email: 'john@example.com'
 * };
 *
 * filterSystemFields(record)
 * // { name: 'John', email: 'john@example.com' }
 *
 * filterSystemFields(record, true)
 * // { _record_id: '123', _version_id: '456', name: 'John', email: 'john@example.com' }
 * ```
 */
export function filterSystemFields(
  record: Record<string, unknown>,
  includeSystemFields = false
): Record<string, unknown> {
  // Early return if no filtering needed
  if (includeSystemFields) {
    return record;
  }

  const filtered: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record)) {
    if (!isSystemField(key)) {
      filtered[key] = value;
    }
  }
  return filtered;
}

/**
 * Filter system fields from multiple records
 *
 * @param records - Array of record objects
 * @param includeSystemFields - If true, keeps system fields (default: false)
 * @returns Array of records with only user fields (unless includeSystemFields is true)
 *
 * @example
 * ```typescript
 * const records = [
 *   { _record_id: '1', name: 'Alice' },
 *   { _record_id: '2', name: 'Bob' }
 * ];
 *
 * filterSystemFieldsFromRecords(records)
 * // [{ name: 'Alice' }, { name: 'Bob' }]
 * ```
 */
export function filterSystemFieldsFromRecords(
  records: Record<string, unknown>[],
  includeSystemFields = false
): Record<string, unknown>[] {
  // Early return if no filtering needed
  if (includeSystemFields) {
    return records;
  }

  return records.map((record) => filterSystemFields(record, false));
}

/**
 * Get only system fields from a record
 * Inverse of filterSystemFields - returns only metadata
 *
 * @param record - Record object with mixed user and system fields
 * @returns Record with only system fields
 *
 * @example
 * ```typescript
 * const record = {
 *   _record_id: '123',
 *   _version_id: '456',
 *   name: 'John',
 *   email: 'john@example.com'
 * };
 *
 * extractSystemFields(record)
 * // { _record_id: '123', _version_id: '456' }
 * ```
 */
export function extractSystemFields(
  record: Record<string, unknown>
): Record<string, unknown> {
  const systemFields: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(record)) {
    if (isSystemField(key)) {
      systemFields[key] = value;
    }
  }
  return systemFields;
}

/**
 * Type guard for checking if a record has all required system fields
 * Useful for validation during version processing
 *
 * @param record - Record to validate
 * @returns True if all required system fields are present
 */
export function hasRequiredSystemFields(
  record: Record<string, unknown>
): boolean {
  return (
    typeof record[SYSTEM_FIELDS.RECORD_ID] === 'string' &&
    typeof record[SYSTEM_FIELDS.VERSION_ID] === 'string' &&
    typeof record[SYSTEM_FIELDS.CREATED_AT] === 'string' &&
    typeof record[SYSTEM_FIELDS.CONTENT_HASH] === 'string' &&
    typeof record[SYSTEM_FIELDS.IS_ACTIVE] === 'boolean'
  );
}
